import { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { MessageSquare, Plus, Send, X, User, Shield } from 'lucide-react';

const STATUS_CONFIG = {
  open:        { label: 'Open',        color: 'bg-red-100 text-red-700'    },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700'  },
  resolved:    { label: 'Resolved',    color: 'bg-green-100 text-green-700'},
  closed:      { label: 'Closed',      color: 'bg-gray-100 text-gray-500'  },
};
const PRIORITY_OPTIONS = ['low','medium','high','urgent'];

function fmt(d) {
  if (!d) return '';
  try {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff/60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff/3600000)}h ago`;
    return date.toLocaleDateString('en-IN',{day:'2-digit',month:'short'});
  } catch { return ''; }
}

export default function ClientTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [msgLoading, setMsgLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject:'', project_id:'', priority:'medium', message:'' });
  const [projects, setProjects] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [creating, setCreating] = useState(false);
  const msgEndRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get('/client/tickets'),
      api.get('/client/projects'),
    ]).then(([t, p]) => {
      setTickets(t.data);
      setProjects(p.data);
    }).catch(() => toast.error('Failed to load'))
    .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openTicket = async (id) => {
    setActiveId(id);
    setMsgLoading(true);
    try {
      const r = await api.get(`/client/tickets/${id}`);
      setActive(r.data);
      setTickets(ts => ts.map(t => t.id===id ? {...t, unread_admin:0} : t));
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior:'smooth' }), 100);
    } catch { toast.error('Failed to load'); }
    finally { setMsgLoading(false); }
  };

  const createTicket = async () => {
    if (!newForm.subject.trim() || !newForm.message.trim()) return toast.error('Subject and message required');
    setCreating(true);
    try {
      const r = await api.post('/client/tickets', newForm);
      toast.success('Support ticket created! Admin will respond soon.');
      setShowNew(false);
      setNewForm({ subject:'', project_id:'', priority:'medium', message:'' });
      load();
      openTicket(r.data.id);
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setCreating(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/client/tickets/${activeId}/messages`, { message: reply.trim() });
      setReply('');
      const r = await api.get(`/client/tickets/${activeId}`);
      setActive(r.data);
      setTimeout(() => msgEndRef.current?.scrollIntoView({ behavior:'smooth' }), 50);
    } catch { toast.error('Send failed'); }
    finally { setSending(false); }
  };

  const totalUnread = tickets.reduce((s,t) => s + (t.unread_admin||0), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-black text-gray-900">Support</h1>
            {totalUnread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                {totalUnread} new
              </span>
            )}
          </div>
          <p className="text-gray-400 text-sm mt-0.5">Chat with our team for queries & support</p>
        </div>
        <button onClick={() => setShowNew(true)} className="btn-primary">
          <Plus size={16}/> New Ticket
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{height:'calc(100vh - 200px)', minHeight:500}}>
        {/* Ticket list */}
        <div className="card overflow-hidden flex flex-col">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-600">{tickets.length} tickets</p>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-6 h-6 border-4 border-amber-500 border-t-transparent rounded-full"/>
              </div>
            ) : tickets.length === 0 ? (
              <div className="py-12 text-center">
                <MessageSquare className="w-10 h-10 text-gray-200 mx-auto mb-2"/>
                <p className="text-gray-400 text-sm">No tickets yet</p>
                <p className="text-gray-300 text-xs mt-1">Create one to get support</p>
              </div>
            ) : tickets.map(t => {
              const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.open;
              return (
                <div key={t.id}
                  onClick={() => openTicket(t.id)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${activeId===t.id?'bg-amber-50 border-l-2 border-amber-400':'hover:bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{t.subject}</p>
                    {t.unread_admin > 0 && (
                      <span className="bg-green-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">{t.unread_admin}</span>
                    )}
                  </div>
                  {t.project_name && <p className="text-xs text-gray-400 mb-1">{t.project_name}</p>}
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${sc.color}`}>{sc.label}</span>
                    <span className="text-xs text-gray-400">{fmt(t.updated_at)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Chat */}
        <div className="lg:col-span-2 card overflow-hidden flex flex-col">
          {!activeId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 text-gray-200 mx-auto mb-3"/>
                <p className="text-gray-400 font-medium">Select a ticket to view chat</p>
                <p className="text-gray-300 text-sm mt-1">Or create a new ticket for support</p>
              </div>
            </div>
          ) : msgLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"/>
            </div>
          ) : active && (
            <>
              <div className="px-5 py-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 text-sm">{active.subject}</h3>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {active.project_name && <span className="text-xs text-gray-400">{active.project_name}</span>}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_CONFIG[active.status]?.color}`}>
                    {STATUS_CONFIG[active.status]?.label}
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {active.messages?.map(m => {
                  const isAdmin = m.sender_role === 'admin';
                  return (
                    <div key={m.id} className={`flex items-end gap-2 ${isAdmin ? 'flex-row' : 'flex-row-reverse'}`}>
                      <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold ${isAdmin ? 'bg-amber-500' : 'bg-blue-500'}`}>
                        {isAdmin ? <Shield size={13}/> : <User size={13}/>}
                      </div>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                        isAdmin ? 'bg-amber-50 text-gray-900 rounded-bl-sm border border-amber-100' : 'bg-blue-500 text-white rounded-br-sm'
                      }`}>
                        {isAdmin && <p className="text-xs font-bold text-amber-600 mb-1">Admin</p>}
                        <p style={{whiteSpace:'pre-wrap'}}>{m.message}</p>
                        <p className={`text-xs mt-1 ${isAdmin ? 'text-gray-400':'text-blue-100'}`}>{fmt(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={msgEndRef}/>
              </div>

              <div className="p-4 border-t border-gray-100">
                {active.status === 'closed' ? (
                  <div className="text-center py-3 text-gray-400 text-sm bg-gray-50 rounded-xl">This ticket is closed</div>
                ) : (
                  <div className="flex gap-2">
                    <textarea
                      value={reply}
                      onChange={e => setReply(e.target.value)}
                      onKeyDown={e => { if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendReply();}}}
                      placeholder="Type message… (Enter to send)"
                      className="field-input flex-1 resize-none"
                      rows={2}
                    />
                    <button onClick={sendReply} disabled={!reply.trim()||sending} className="btn-primary flex-shrink-0 self-end" style={{padding:'10px 16px'}}>
                      <Send size={16}/>
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New Ticket Modal */}
      {showNew && (
        <div className="modal-backdrop">
          <div className="modal-scroll">
            <div className="modal-box" style={{maxWidth:500}}>
              <div className="modal-header">
                <div><h2>New Support Ticket</h2><p>Describe your query or issue</p></div>
                <button className="modal-close" onClick={()=>setShowNew(false)}><X size={16}/></button>
              </div>
              <div className="modal-body space-y-4">
                <div>
                  <label className="field-label">Subject *</label>
                  <input className="field-input" placeholder="Brief description of your query" value={newForm.subject} onChange={e=>setNewForm(f=>({...f,subject:e.target.value}))}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="field-label">Related Project</label>
                    <select className="field-input" value={newForm.project_id} onChange={e=>setNewForm(f=>({...f,project_id:e.target.value}))}>
                      <option value="">General Query</option>
                      {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Priority</label>
                    <select className="field-input" value={newForm.priority} onChange={e=>setNewForm(f=>({...f,priority:e.target.value}))}>
                      {PRIORITY_OPTIONS.map(p=><option key={p} value={p} className="capitalize">{p}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="field-label">Message *</label>
                  <textarea className="field-input" rows={4} style={{resize:'none'}} placeholder="Describe your query in detail…" value={newForm.message} onChange={e=>setNewForm(f=>({...f,message:e.target.value}))}/>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn-secondary" style={{flex:1}} onClick={()=>setShowNew(false)}>Cancel</button>
                <button className="btn-primary" style={{flex:1}} onClick={createTicket} disabled={creating}>
                  {creating ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
